import * as SuperTokens from 'supertokens-web-js'
import * as Session from 'supertokens-web-js/recipe/session'
import * as Passwordless from 'supertokens-web-js/recipe/passwordless'
import { useLogger } from '~/composables/useLogger'

export default defineNuxtPlugin(() => {
  // Ensure we're on client side
  if (import.meta.server) return

  const config = useRuntimeConfig()
  const logger = useLogger()

  // Wait for window to be defined
  if (typeof window !== 'undefined') {
    try {
      SuperTokens.init({
        appInfo: {
          apiDomain: config.public.apiBase,
          apiBasePath: '/auth',
          appName: 'grep',
        },
        recipeList: [
          Session.init({
            override: {
              functions: (originalImplementation) => {
                return {
                  ...originalImplementation,
                  addFetchInterceptorsAndReturnModifiedFetch: (input) => {
                    // Get the authenticated fetch from SuperTokens
                    const authenticatedFetch =
                      originalImplementation.addFetchInterceptorsAndReturnModifiedFetch(input)

                    // Store it globally for manual API calls
                    if (typeof window !== 'undefined') {
                      ;(
                        window as Window & { authenticatedFetch?: typeof fetch }
                      ).authenticatedFetch = authenticatedFetch
                    }

                    // Store reference to original fetch from the input
                    const originalFetch = input.originalFetch

                    // Create a highly selective fetch that only intercepts our auth API
                    const selectiveFetch: typeof fetch = async (
                      requestInput: RequestInfo | URL,
                      init?: RequestInit
                    ) => {
                      const url =
                        typeof requestInput === 'string'
                          ? requestInput
                          : requestInput instanceof URL
                            ? requestInput.href
                            : requestInput.url

                      // Only intercept requests to our auth API domain
                      if (url.includes(config.public.apiBase)) {
                        return authenticatedFetch(requestInput, init)
                      }

                      // Use original fetch for everything else (Apple Maps, CDNs, etc.)
                      return originalFetch(requestInput, init)
                    }

                    return selectiveFetch
                  },
                  addXMLHttpRequestInterceptor: (_input) => {
                    // Completely disable XMLHttpRequest interception to prevent Apple Maps conflicts
                    return undefined
                  },
                }
              },
            },
          }),
          Passwordless.init(),
        ],
      })
      logger.log('SuperTokens initialized with minimal Apple Maps interference')
    } catch (err) {
      // Only log if it's not an "already initialized" error
      if (err instanceof Error && !err.message?.includes('already initialised')) {
        logger.logError('SuperTokens initialization error:', err)
      }
    }
  }
})
